import type { CVEditingSection } from '@cibli/shared';
import type { ComponentType, JSX } from 'react';

import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { TemplateDisplayProps } from '../types';
import type { JsonResumeParams } from './schema';

import { CvThemeShadowRenderer } from '../../../cv-theme-picker/CvThemeShadowRenderer';
import { fetchCvThemePreviewHtml } from '../../../cv-theme-picker/use-cv-themes';
import { usePdfPrintReady } from '../../hooks/usePdfPrintReady';

/**
 * Creates a Display component for a specific JSON Resume theme.
 * Uses CvThemeShadowRenderer for CSS isolation via iframe srcDoc.
 */
export function createJsonResumeDisplay(
  themeId: string,
): ComponentType<TemplateDisplayProps<JsonResumeParams>> {
  function JsonResumeDisplay({
    cv,
    params,
    mode = 'preview',
    onEdit,
  }: TemplateDisplayProps<JsonResumeParams>): JSX.Element | null {
    const [html, setHtml] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    usePdfPrintReady(mode === 'print', containerRef, isLayoutReady);

    useEffect((): (() => void) | undefined => {
      if (!cv) {
        setHtml(null);
        setIsLoading(false);
        setIsLayoutReady(false);
        setError(null);
        return;
      }

      let cancelled = false;

      setIsLoading(true);
      setIsLayoutReady(false);
      setError(null);

      void fetchCvThemePreviewHtml(cv, themeId, params)
        .then((nextHtml: string): void => {
          if (!cancelled) {
            setHtml(nextHtml);
          }
        })
        .catch((err: unknown): void => {
          console.error('JsonResume render failed:', err);
          if (!cancelled) {
            setError('Erreur lors du rendu du thème');
          }
        })
        .finally((): void => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });

      return (): void => {
        cancelled = true;
      };
    }, [cv, params, themeId]);

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }
    if (error) return <div className="py-10 text-center text-sm text-red-500">{error}</div>;
    if (!html) return null;

    return (
      <div ref={containerRef} className="w-full">
        <CvThemeShadowRenderer
          html={html}
          isLoading={isLoading}
          editable={mode === 'edit' && !!onEdit}
          onSectionClick={onEdit ? section => onEdit(section as CVEditingSection) : undefined}
          onLayoutReady={(): void => setIsLayoutReady(true)}
        />
      </div>
    );
  }

  JsonResumeDisplay.displayName = `JsonResumeDisplay(${themeId})`;
  return JsonResumeDisplay;
}
